// Payment Request Service for BookAPT
// Handles payment requests via SMS and chat

import SimpleStorage from '../utils/simpleStorage';
import { Platform } from 'react-native';

export interface PaymentRequest {
  id: string;
  businessId: string;
  customerId: string;
  customerPhone: string;
  customerName: string;
  serviceName: string;
  amount: number;
  currency: string;
  description: string;
  paymentMethods: PaymentMethodType[];
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  deliveryMethod: 'sms' | 'chat' | 'both';
  paymentLink: string;
  stripePaymentIntentId: string;
  createdAt: string;
  expiresAt: string;
  paidAt?: string;
  paymentMethod?: PaymentMethodType;
  transactionId?: string;
}

export interface PaymentMethodType {
  id: string;
  name: string;
  type: 'digital_wallet' | 'card' | 'bank_transfer' | 'other';
  icon: string;
  enabled: boolean;
}

export interface PaymentRequestTemplate {
  id: string;
  name: string;
  type: 'sms' | 'chat';
  template: string;
  variables: string[];
  isActive: boolean;
}

export interface PaymentRequestAnalytics {
  totalRequests: number;
  paidRequests: number;
  pendingRequests: number;
  expiredRequests: number;
  cancelledRequests: number;
  averagePaymentTime: number; // in minutes
  conversionRate: number; // percentage
  paymentMethodBreakdown: { [key: string]: number };
  deliveryMethodBreakdown: { [key: string]: number };
  averageAmount: number;
  totalRevenue: number;
}

class PaymentRequestService {
  private readonly PAYMENT_REQUESTS_KEY = 'payment_requests';
  private readonly PAYMENT_TEMPLATES_KEY = 'payment_templates';
  private readonly PAYMENT_ANALYTICS_KEY = 'payment_analytics';

  // Available payment methods
  static readonly PAYMENT_METHODS: PaymentMethodType[] = [
    {
      id: 'apple_pay',
      name: 'Apple Pay',
      type: 'digital_wallet',
      icon: '🍎',
      enabled: true
    },
    {
      id: 'google_pay',
      name: 'Google Pay',
      type: 'digital_wallet',
      icon: '📱',
      enabled: true
    },
    {
      id: 'venmo',
      name: 'Venmo',
      type: 'digital_wallet',
      icon: '💚',
      enabled: true
    },
    {
      id: 'cash_app',
      name: 'Cash App',
      type: 'digital_wallet',
      icon: '💚',
      enabled: true
    },
    {
      id: 'zelle',
      name: 'Zelle',
      type: 'bank_transfer',
      icon: '🏦',
      enabled: true
    },
    {
      id: 'credit_card',
      name: 'Credit Card',
      type: 'card',
      icon: '💳',
      enabled: true
    },
    {
      id: 'debit_card',
      name: 'Debit Card',
      type: 'card',
      icon: '💳',
      enabled: true
    }
  ];

  // Default payment request templates
  private readonly DEFAULT_TEMPLATES: PaymentRequestTemplate[] = [
    {
      id: 'sms_payment_request',
      name: 'SMS Payment Request',
      type: 'sms',
      template: '🏪 {businessName}\n💳 Payment Request\n\nService: {serviceName}\nAmount: {amount}\n\nPay securely: {paymentLink}\n\nExpires: {expirationTime}\n\nReply STOP to opt out',
      variables: ['businessName', 'serviceName', 'amount', 'paymentLink', 'expirationTime'],
      isActive: true
    },
    {
      id: 'chat_payment_request',
      name: 'Chat Payment Request',
      type: 'chat',
      template: '💳 Payment Request from {businessName}\n\nService: {serviceName}\nAmount: {amount}\nDescription: {description}\n\nChoose your payment method:\n{paymentMethods}\n\nPayment expires in 24 hours.',
      variables: ['businessName', 'serviceName', 'amount', 'description', 'paymentMethods'],
      isActive: true
    },
    {
      id: 'payment_reminder',
      name: 'Payment Reminder',
      type: 'sms',
      template: '⏰ Payment Reminder\n\n{businessName} - {serviceName}\nAmount: {amount}\n\nPay: {paymentLink}\n\nExpires soon!',
      variables: ['businessName', 'serviceName', 'amount', 'paymentLink'],
      isActive: true
    },
    {
      id: 'payment_confirmation',
      name: 'Payment Confirmation',
      type: 'sms',
      template: '✅ Payment Confirmed\n\n{businessName}\nAmount: {amount}\nDate: {paymentDate}\nTransaction: {transactionId}\n\nThank you!',
      variables: ['businessName', 'amount', 'paymentDate', 'transactionId'],
      isActive: true
    }
  ];

  constructor() {
    this.initializeTemplates();
  }

  // Initialize default templates
  private async initializeTemplates() {
    try {
      const existingTemplates = await this.getTemplates();
      if (existingTemplates.length === 0) {
        await this.saveTemplates(this.DEFAULT_TEMPLATES);
      }
    } catch (error) {
      console.error('Error initializing payment templates:', error);
    }
  }

  // Create a new payment request
  async createPaymentRequest(requestData: {
    businessId: string;
    customerId: string;
    customerPhone: string;
    customerName: string;
    serviceName: string;
    amount: number;
    description: string;
    paymentMethods: string[];
    deliveryMethod: 'sms' | 'chat' | 'both';
    expirationHours?: number;
  }): Promise<PaymentRequest | null> {
    try {
      const paymentRequest: PaymentRequest = {
        id: `pr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        businessId: requestData.businessId,
        customerId: requestData.customerId,
        customerPhone: requestData.customerPhone,
        customerName: requestData.customerName,
        serviceName: requestData.serviceName,
        amount: requestData.amount,
        currency: 'USD',
        description: requestData.description,
        paymentMethods: this.getPaymentMethodsByIds(requestData.paymentMethods),
        status: 'pending',
        deliveryMethod: requestData.deliveryMethod,
        paymentLink: '', // Will be generated
        stripePaymentIntentId: '', // Will be generated
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + (requestData.expirationHours || 24) * 60 * 60 * 1000).toISOString()
      };

      // Generate payment link
      paymentRequest.paymentLink = await this.generatePaymentLink(paymentRequest);
      
      // Create Stripe payment intent
      paymentRequest.stripePaymentIntentId = await this.createStripePaymentIntent(paymentRequest);

      // Save payment request
      await this.savePaymentRequest(paymentRequest);

      // Send payment request
      await this.sendPaymentRequest(paymentRequest);

      return paymentRequest;
    } catch (error) {
      console.error('Error creating payment request:', error);
      return null;
    }
  }

  // Send payment request via SMS or chat
  private async sendPaymentRequest(paymentRequest: PaymentRequest): Promise<boolean> {
    try {
      if (paymentRequest.deliveryMethod === 'sms' || paymentRequest.deliveryMethod === 'both') {
        await this.sendSMSPaymentRequest(paymentRequest);
      }

      if (paymentRequest.deliveryMethod === 'chat' || paymentRequest.deliveryMethod === 'both') {
        await this.sendChatPaymentRequest(paymentRequest);
      }

      return true;
    } catch (error) {
      console.error('Error sending payment request:', error);
      return false;
    }
  }

  // Send SMS payment request
  private async sendSMSPaymentRequest(paymentRequest: PaymentRequest): Promise<boolean> {
    try {
      const template = await this.getTemplate('sms_payment_request');
      if (!template) {
        throw new Error('SMS payment request template not found');
      }

      const message = this.replaceTemplateVariables(template.template, {
        businessName: 'Elite Hair Studio', // Get from business data
        serviceName: paymentRequest.serviceName,
        amount: paymentRequest.amount.toString(),
        paymentLink: paymentRequest.paymentLink,
        expirationTime: new Date(paymentRequest.expiresAt).toLocaleDateString()
      });

      // Import and use SMS service
      const { smsService } = await import('./smsService');
      return await smsService.sendSMS(paymentRequest.customerPhone, message, 'general');
    } catch (error) {
      console.error('Error sending SMS payment request:', error);
      return false;
    }
  }

  // Send chat payment request
  private async sendChatPaymentRequest(paymentRequest: PaymentRequest): Promise<boolean> {
    try {
      const template = await this.getTemplate('chat_payment_request');
      if (!template) {
        throw new Error('Chat payment request template not found');
      }

      const paymentMethodsText = paymentRequest.paymentMethods
        .map(method => `${method.icon} ${method.name}`)
        .join('\n');

      const message = this.replaceTemplateVariables(template.template, {
        businessName: 'Elite Hair Studio', // Get from business data
        serviceName: paymentRequest.serviceName,
        amount: paymentRequest.amount.toString(),
        description: paymentRequest.description,
        paymentMethods: paymentMethodsText
      });

      // Send chat message (integrate with existing chat system)
      await this.sendChatMessage({
        customerId: paymentRequest.customerId,
        businessId: paymentRequest.businessId,
        message: {
          type: 'payment_request',
          text: message,
          paymentRequestId: paymentRequest.id,
          amount: paymentRequest.amount,
          paymentMethods: paymentRequest.paymentMethods,
          paymentLink: paymentRequest.paymentLink
        }
      });

      return true;
    } catch (error) {
      console.error('Error sending chat payment request:', error);
      return false;
    }
  }

  // Process payment
  async processPayment(paymentRequestId: string, paymentMethod: string, paymentData?: any): Promise<boolean> {
    try {
      const paymentRequest = await this.getPaymentRequest(paymentRequestId);
      if (!paymentRequest) {
        throw new Error('Payment request not found');
      }

      if (paymentRequest.status !== 'pending') {
        throw new Error('Payment request is not pending');
      }

      if (new Date() > new Date(paymentRequest.expiresAt)) {
        paymentRequest.status = 'expired';
        await this.updatePaymentRequest(paymentRequest);
        throw new Error('Payment request has expired');
      }

      // Process payment with Stripe
      const paymentResult = await this.processStripePayment(paymentRequest, paymentMethod, paymentData);

      if (paymentResult.success) {
        paymentRequest.status = 'paid';
        paymentRequest.paidAt = new Date().toISOString();
        paymentRequest.paymentMethod = this.getPaymentMethodById(paymentMethod);
        paymentRequest.transactionId = paymentResult.transactionId;

        await this.updatePaymentRequest(paymentRequest);

        // Send confirmation
        await this.sendPaymentConfirmation(paymentRequest);

        return true;
      } else {
        throw new Error(paymentResult.error || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      return false;
    }
  }

  // Generate payment link
  private async generatePaymentLink(paymentRequest: PaymentRequest): Promise<string> {
    // In production, this would generate a secure payment link
    // For demo, we'll create a simple link
    return `https://bookapt.io/pay/${paymentRequest.id}`;
  }

  // Create Stripe payment intent
  private async createStripePaymentIntent(paymentRequest: PaymentRequest): Promise<string> {
    // In production, this would create a real Stripe payment intent
    // For demo, we'll return a mock ID
    return `pi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Process Stripe payment
  private async processStripePayment(paymentRequest: PaymentRequest, paymentMethod: string, paymentData?: any): Promise<{ success: boolean; transactionId?: string; error?: string }> {
    // In production, this would process the actual payment with Stripe
    // For demo, we'll simulate a successful payment
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          transactionId: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      }, 2000);
    });
  }

  // Send payment confirmation
  private async sendPaymentConfirmation(paymentRequest: PaymentRequest): Promise<boolean> {
    try {
      const template = await this.getTemplate('payment_confirmation');
      if (!template) {
        throw new Error('Payment confirmation template not found');
      }

      const message = this.replaceTemplateVariables(template.template, {
        businessName: 'Elite Hair Studio',
        amount: paymentRequest.amount.toString(),
        paymentDate: new Date(paymentRequest.paidAt!).toLocaleDateString(),
        transactionId: paymentRequest.transactionId!
      });

      // Send SMS confirmation
      const { smsService } = await import('./smsService');
      return await smsService.sendSMS(paymentRequest.customerPhone, message, 'general');
    } catch (error) {
      console.error('Error sending payment confirmation:', error);
      return false;
    }
  }

  // Send chat message (integrate with existing chat system)
  private async sendChatMessage(messageData: any): Promise<void> {
    // This would integrate with the existing chat system
    console.log('Sending chat message:', messageData);
  }

  // Get payment request by ID
  async getPaymentRequest(paymentRequestId: string): Promise<PaymentRequest | null> {
    try {
      const paymentRequests = await this.getPaymentRequests();
      return paymentRequests.find(pr => pr.id === paymentRequestId) || null;
    } catch (error) {
      console.error('Error getting payment request:', error);
      return null;
    }
  }

  // Get all payment requests (Firestore first, then local fallback)
  async getPaymentRequests(): Promise<PaymentRequest[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedRequests: PaymentRequest[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { collection, getDocs } = firestoreMod;
          const db = getWebDb();
          
          if (db) {
            const requestsSnapshot = await getDocs(collection(db, 'payment_requests'));
            loadedRequests = requestsSnapshot.docs.map((snap: any) => ({
              id: snap.id,
              ...snap.data()
            } as PaymentRequest));
            loadedFromFirestore = true;
            console.log(`✅ PaymentRequestService: Loaded ${loadedRequests.length} payment requests from Firestore`);
            
            // Sync to local storage for offline access
            await SimpleStorage.setItem(this.PAYMENT_REQUESTS_KEY, JSON.stringify(loadedRequests));
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  PaymentRequestService: Could not load requests from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const requestsJson = await SimpleStorage.getItem(this.PAYMENT_REQUESTS_KEY);
        if (requestsJson) {
          loadedRequests = JSON.parse(requestsJson);
          console.log(`📋 PaymentRequestService: Loaded ${loadedRequests.length} payment requests from local storage`);
        }
      }
      
      return loadedRequests;
    } catch (error) {
      console.error('Error getting payment requests:', error);
      return [];
    }
  }

  // Save payment request (Firestore + local storage)
  private async savePaymentRequest(paymentRequest: PaymentRequest): Promise<void> {
    try {
      const paymentRequests = await this.getPaymentRequests();
      paymentRequests.push(paymentRequest);
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.PAYMENT_REQUESTS_KEY, JSON.stringify(paymentRequests));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  PaymentRequestService: Firestore module unavailable, skipping Firestore save');
            return;
          }
          const { doc, setDoc } = firestoreMod;
          const db = getWebDb();
          
          if (db) {
            await setDoc(doc(db, 'payment_requests', paymentRequest.id), {
              ...paymentRequest,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ PaymentRequestService: Payment request saved to Firestore: ${paymentRequest.id}`);
          }
        } catch (firestoreError: any) {
          console.error('❌ PaymentRequestService: Error saving payment request to Firestore:', firestoreError);
          console.warn('⚠️  Payment request saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving payment request:', error);
    }
  }

  // Update payment request (Firestore + local storage)
  private async updatePaymentRequest(paymentRequest: PaymentRequest): Promise<void> {
    try {
      const paymentRequests = await this.getPaymentRequests();
      const index = paymentRequests.findIndex(pr => pr.id === paymentRequest.id);
      if (index !== -1) {
        paymentRequests[index] = paymentRequest;
        
        // Save to local storage (for offline access)
        await SimpleStorage.setItem(this.PAYMENT_REQUESTS_KEY, JSON.stringify(paymentRequests));
        
        // Also update in Firestore (for cross-browser/device access)
        if (Platform.OS === 'web') {
          try {
            const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
            const firestoreMod = getFirestoreFunctions?.();
            if (!firestoreMod) {
              return;
            }
            const { doc, updateDoc } = firestoreMod;
            const db = getWebDb();
            
            if (db) {
              await updateDoc(doc(db, 'payment_requests', paymentRequest.id), {
                ...paymentRequest,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ PaymentRequestService: Payment request updated in Firestore: ${paymentRequest.id}`);
            }
          } catch (firestoreError: any) {
            console.error('❌ PaymentRequestService: Error updating payment request in Firestore:', firestoreError);
            // Don't throw - local update succeeded
          }
        }
      }
    } catch (error) {
      console.error('Error updating payment request:', error);
    }
  }

  // Get payment methods by IDs
  private getPaymentMethodsByIds(ids: string[]): PaymentMethodType[] {
    return PaymentRequestService.PAYMENT_METHODS.filter(method => 
      ids.includes(method.id) && method.enabled
    );
  }

  // Get payment method by ID
  private getPaymentMethodById(id: string): PaymentMethodType | undefined {
    return PaymentRequestService.PAYMENT_METHODS.find(method => method.id === id);
  }

  // Get templates (Firestore first, then local fallback)
  async getTemplates(): Promise<PaymentRequestTemplate[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedTemplates: PaymentRequestTemplate[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { collection, getDocs, query, where, doc } = firestoreMod;
          
          // Get Firestore from the shared web Firebase instance (auto-initializes if needed)
          const firestoreDb = getWebDb();
          if (!firestoreDb) {
            console.warn('⚠️  PaymentRequestService: Firebase webDb not available, skipping Firestore load');
            throw new Error('webDb unavailable');
          }

          // Get current business owner to filter templates
          const businessOwner = await AuthManager.getSavedBusinessOwner();
          if (businessOwner) {
            // Use collection() and doc() properly for subcollections
            const businessDocRef = doc(firestoreDb, 'businesses', businessOwner.id);
            const templatesRef = collection(businessDocRef, 'payment_templates');
            const templatesQuery = query(
              templatesRef,
              where('isActive', '==', true)
            );
            const templatesSnapshot = await getDocs(templatesQuery);
            loadedTemplates = templatesSnapshot.docs.map((snap: any) => ({
              id: snap.id,
              ...snap.data()
            } as PaymentRequestTemplate));
            loadedFromFirestore = true;
            console.log(`✅ PaymentRequestService: Loaded ${loadedTemplates.length} templates from Firestore`);
            
            // Sync to local storage
            await SimpleStorage.setItem(this.PAYMENT_TEMPLATES_KEY, JSON.stringify(loadedTemplates));
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  PaymentRequestService: Could not load templates from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const templatesJson = await SimpleStorage.getItem(this.PAYMENT_TEMPLATES_KEY);
        if (templatesJson) {
          loadedTemplates = JSON.parse(templatesJson);
          console.log(`📋 PaymentRequestService: Loaded ${loadedTemplates.length} templates from local storage`);
        }
      }
      
      return loadedTemplates;
    } catch (error) {
      console.error('Error getting payment templates:', error);
      return [];
    }
  }

  // Get specific template
  async getTemplate(templateId: string): Promise<PaymentRequestTemplate | null> {
    try {
      const templates = await this.getTemplates();
      return templates.find(t => t.id === templateId) || null;
    } catch (error) {
      console.error('Error getting payment template:', error);
      return null;
    }
  }

  // Save templates (Firestore + local storage)
  private async saveTemplates(templates: PaymentRequestTemplate[]): Promise<void> {
    try {
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.PAYMENT_TEMPLATES_KEY, JSON.stringify(templates));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getWebDb, getFirestoreFunctions } = require('./unifiedAuthService');
          const { AuthManager } = await import('../utils/authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  PaymentRequestService: Firestore module unavailable, skipping Firestore save');
            return;
          }
          const { doc, setDoc, collection } = firestoreMod;
          
          // Get Firestore from the shared web Firebase instance (auto-initializes if needed)
          const firestoreDb = getWebDb();
          if (!firestoreDb) {
            console.warn('⚠️  PaymentRequestService: Firebase webDb not available, skipping Firestore save');
            return;
          }

          const businessOwner = await AuthManager.getSavedBusinessOwner();
          if (businessOwner) {
            // Use collection() and doc() properly for subcollections
            const businessDocRef = doc(firestoreDb, 'businesses', businessOwner.id);
            const templatesRef = collection(businessDocRef, 'payment_templates');
            
            // Save each template as a separate document
            for (const template of templates) {
              const templateDocRef = doc(templatesRef, template.id);
              await setDoc(templateDocRef, {
                ...template,
                updatedAt: new Date().toISOString(),
              });
            }
            console.log(`✅ PaymentRequestService: ${templates.length} templates saved to Firestore`);
          }
        } catch (firestoreError: any) {
          console.error('❌ PaymentRequestService: Error saving templates to Firestore:', firestoreError);
          console.warn('⚠️  Templates saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving payment templates:', error);
    }
  }

  // Replace template variables
  private replaceTemplateVariables(template: string, variables: { [key: string]: string }): string {
    let message = template;
    Object.keys(variables).forEach(key => {
      const placeholder = `{${key}}`;
      message = message.replace(new RegExp(placeholder, 'g'), variables[key]);
    });
    return message;
  }

  // Get analytics
  async getAnalytics(): Promise<PaymentRequestAnalytics> {
    try {
      const paymentRequests = await this.getPaymentRequests();
      
      const analytics: PaymentRequestAnalytics = {
        totalRequests: paymentRequests.length,
        paidRequests: paymentRequests.filter(pr => pr.status === 'paid').length,
        pendingRequests: paymentRequests.filter(pr => pr.status === 'pending').length,
        expiredRequests: paymentRequests.filter(pr => pr.status === 'expired').length,
        cancelledRequests: paymentRequests.filter(pr => pr.status === 'cancelled').length,
        averagePaymentTime: 0,
        conversionRate: 0,
        paymentMethodBreakdown: {},
        deliveryMethodBreakdown: {},
        averageAmount: 0,
        totalRevenue: 0
      };

      // Calculate conversion rate
      if (analytics.totalRequests > 0) {
        analytics.conversionRate = (analytics.paidRequests / analytics.totalRequests) * 100;
      }

      // Calculate average amount
      if (paymentRequests.length > 0) {
        analytics.averageAmount = paymentRequests.reduce((sum, pr) => sum + pr.amount, 0) / paymentRequests.length;
      }

      // Calculate total revenue
      analytics.totalRevenue = paymentRequests
        .filter(pr => pr.status === 'paid')
        .reduce((sum, pr) => sum + pr.amount, 0);

      // Calculate payment method breakdown
      paymentRequests.forEach(pr => {
        if (pr.paymentMethod) {
          analytics.paymentMethodBreakdown[pr.paymentMethod.name] = 
            (analytics.paymentMethodBreakdown[pr.paymentMethod.name] || 0) + 1;
        }
      });

      // Calculate delivery method breakdown
      paymentRequests.forEach(pr => {
        analytics.deliveryMethodBreakdown[pr.deliveryMethod] = 
          (analytics.deliveryMethodBreakdown[pr.deliveryMethod] || 0) + 1;
      });

      return analytics;
    } catch (error) {
      console.error('Error getting payment analytics:', error);
      return {
        totalRequests: 0,
        paidRequests: 0,
        pendingRequests: 0,
        expiredRequests: 0,
        cancelledRequests: 0,
        averagePaymentTime: 0,
        conversionRate: 0,
        paymentMethodBreakdown: {},
        deliveryMethodBreakdown: {},
        averageAmount: 0,
        totalRevenue: 0
      };
    }
  }

  // Get available payment methods
  static getAvailablePaymentMethods(): PaymentMethodType[] {
    return PaymentRequestService.PAYMENT_METHODS.filter(method => method.enabled);
  }
}

export const paymentRequestService = new PaymentRequestService();
export default PaymentRequestService;



